// Per-contract lot QC summary — ported from the prototype's
// `lotQCSummary(lot, { contractId })`. Used by the New OT wizard's lot
// step to surface "Rejected by this client" / "Not accepted by this
// client" / "No QC yet" warnings on each candidate lot.
//
// Backend QC enums are upper-case (`ACCEPTED` / `REJECTED` / `PENDING`,
// `LAB` / `CLIENT`); the prototype was lower-case. We compare on the
// uppercase wire shape directly.

import type { LotListItemWithQC } from "@/features/lots/api/types"

// `LotListItem` in v2 doesn't have `qcLogs` baked in — when the list is
// fetched with `?includeQc=true` the backend appends them. The summary
// reads them as an optional field so callers can pass either shape.
export type LotWithQC = LotListItemWithQC

export interface LotQCSummary {
  // Any client log marked the lot as accepted for THIS contract.
  contractAccepted: boolean
  // Any client log marked the lot as rejected for THIS contract.
  contractRejected: boolean
  // No client logs at all for this lot — neither contract-scoped nor general.
  untested: boolean
  // Convenience: there's at least one client log, but none scoped to this
  // contract. Useful for the "Not accepted by this client" warning.
  hasOtherClientLogs: boolean
}

export function lotQCSummary(
  lot: LotWithQC,
  opts: { contractId?: number | null } = {},
): LotQCSummary {
  const logs = lot.qcLogs ?? []
  const clientLogs = logs.filter((l) => l.kind === "CLIENT")

  const contractScoped = opts.contractId
    ? clientLogs.filter((l) => l.contractId === opts.contractId)
    : []

  const contractAccepted = contractScoped.some((l) => l.decision === "ACCEPTED")
  const contractRejected = contractScoped.some((l) => l.decision === "REJECTED")
  const untested = clientLogs.length === 0
  const hasOtherClientLogs =
    clientLogs.length > 0 && contractScoped.length === 0

  return {
    contractAccepted,
    contractRejected,
    untested,
    hasOtherClientLogs,
  }
}

// Returns a stable warning code for the lot relative to a contract — or
// `null` if the lot is in good shape. Components map this to a chip
// label via the `transit:qcWarnings.<code>` i18n key.
export type LotQCWarning =
  | "rejected"
  | "noQc"
  | "notAcceptedByThisClient"

export function lotQCWarning(
  lot: LotWithQC,
  contractId: number | null,
): LotQCWarning | null {
  const s = lotQCSummary(lot, { contractId })
  if (s.contractRejected) return "rejected"
  if (s.contractAccepted) return null
  if (s.untested) return "noQc"
  if (s.hasOtherClientLogs) return "notAcceptedByThisClient"
  return null
}
